// services/datamapping.service.ts
export type MapItem = { available: string; selected: string; error?: boolean };
export type SheetHeader = { value: string; taken: boolean };
export type ErrorMsg = { name: string; shown: boolean };

const AVAILABLE_KEYS = [
  "Name",
  "Last Name",
  "Email",
  "Phone",
  "Whatsapp Number",
  "Messenger",
] as const;

const DEFAULT_ERROR_MSG: ErrorMsg[] = [
  { name: "Email Must Be Selected", shown: false },
  { name: "Phone Must Be Selected", shown: false },
];

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

function computeNext(mapData: MapItem[]): boolean {
  const emailIdx = AVAILABLE_KEYS.findIndex((k) => k === "Email");
  return emailIdx >= 0 ? !!mapData[emailIdx]?.selected : false;
}

type ChangeArgs = {
  dItem: { value: string };
  dIndex: number;
  index: number;
  prevSelected: string;         // formerly dataItem
  clicked: number;              // non-zero triggers checkDataMapping
  mapData: MapItem[];
  sheetHeaders: SheetHeader[];
};

class DataMappingService {
  /** Build fresh mapping & headers from incoming header row. */
  setDefaultMappedDAta(headers: string[]) {
    const mapData: MapItem[] = (AVAILABLE_KEYS as readonly string[]).map((k) => ({
      available: k,
      selected: "",
      error: false,
    }));

    const sheetHeaders: SheetHeader[] = (headers || []).map((h) => ({
      value: String(h),
      taken: false,
    }));

    return { mapData, sheetHeaders, errorMsg: clone(DEFAULT_ERROR_MSG) };
  }

  /** Back-compat alias. */
  buildDefaultMapping(headers: string[]) {
    return this.setDefaultMappedDAta(headers);
  }

  /**
   * Update a single mapping row.
   * Supports BOTH:
   *   - changeAvailableValues({ dItem, dIndex, index, prevSelected, clicked, mapData, sheetHeaders })
   *   - changeAvailableValues(dItem, dIndex, index, dataItem, clicked, mapData, sheetHeaders)
   */
  changeAvailableValues(
    a:
      | ChangeArgs
      | { value: string },
    b?: number,
    c?: number,
    d?: string,
    e?: number,
    f?: MapItem[],
    g?: SheetHeader[]
  ): { data: MapItem[]; sheetHeaders: SheetHeader[]; next: boolean } {
    // normalize inputs
    let dItem: { value: string };
    let dIndex: number;
    let index: number;
    let prevSelected: string;
    let clicked: number;
    let mapDataIn: MapItem[];
    let sheetHeadersIn: SheetHeader[];

    if (typeof (a as any).dItem === "object") {
      // object style
      const args = a as ChangeArgs;
      ({ dItem, dIndex, index } = args);
      prevSelected = args.prevSelected || "";
      clicked = args.clicked || 0;
      mapDataIn = args.mapData || [];
      sheetHeadersIn = args.sheetHeaders || [];
    } else {
      // positional style
      dItem = a as { value: string };
      dIndex = b as number;
      index = c as number;
      prevSelected = d || "";
      clicked = e || 0;
      mapDataIn = f || [];
      sheetHeadersIn = g || [];
    }

    const mapData = clone(mapDataIn);
    const sheetHeaders = clone(sheetHeadersIn);

    // apply new selection
    if (mapData[index]) mapData[index].selected = dItem?.value ?? "";

    // mark new header taken
    if (dIndex >= 0 && sheetHeaders[dIndex]) sheetHeaders[dIndex].taken = true;

    // free previously selected header, if any
    if (prevSelected && prevSelected.length > 0) {
      const prevIdx = sheetHeaders.findIndex((h) => h.value === prevSelected);
      if (prevIdx >= 0) sheetHeaders[prevIdx].taken = false;
    }

    const finalMap = clicked ? this.checkDataMapping(mapData) : mapData;

    return {
      data: finalMap,
      sheetHeaders,
      next: computeNext(finalMap),
    };
  }

  /** Mark error=true on the "Email" row if not selected. */
  checkDataMapping(mapDataIn: MapItem[]): MapItem[] {
    const mapData = clone(mapDataIn || []);
    for (const item of mapData) {
      if (item.available === "Email") {
        item.error = !item.selected;
      }
    }
    return mapData;
  }

  /** Return empty structures for a clean reset. */
  revertMapData() {
    return {
      mapData: [],
      sheetHeaders: [],
      errorMsg: clone(DEFAULT_ERROR_MSG),
    };
  }

  /** Map raw rows -> unified objects, based on current mapping. */
  validateKeys(mapDataIn: MapItem[], rows: any[]): any[] {
    const mapData = clone(mapDataIn || []);
    const headerMap = new Map<string, string>();
    for (const m of mapData) {
      if (m.available && m.selected) headerMap.set(m.available, m.selected);
    }
    return (rows || []).map((row) => {
      const obj: Record<string, any> = {};
      for (const [avail, sel] of headerMap.entries()) {
        obj[avail] = row?.[sel] ?? null;
      }
      return obj;
    });
  }

  get availableKeys(): string[] {
    return clone(AVAILABLE_KEYS as unknown as string[]);
  }
  get defaultErrorMsg(): ErrorMsg[] {
    return clone(DEFAULT_ERROR_MSG);
  }
}

export default new DataMappingService();
